// sim/geneData.ts — the gene CATALOGUE: names, glyphs, descriptions, effect
// descriptors, and procedural weights for the gene-loot system. Pure data, no
// logic. Indexed positionally by the GENE_TYPE / GENE_TRAIT / RARITY /
// EFFECT_CHANNEL / CASTE constants in ../types.ts (that order is a hard contract).
// Grounded in docs/LORE.md §"Genes — evolution as modular loot".
import { EFFECT_CHANNEL, CASTE_COUNT } from '../types.ts';
import type { EffectChannel } from '../types.ts';

// ---------------------------------------------------------------------------
// Base types (body parts), indexed by GENE_TYPE
// Wing=0 Mandible=1 VenomGland=2 Carapace=3 Antenna=4 Limb=5
// ---------------------------------------------------------------------------

export const GENE_TYPE_NAME: readonly string[] = [
  'Wing',
  'Mandible',
  'Venom Gland',
  'Carapace',
  'Antenna',
  'Limb',
];

export const GENE_TYPE_GLYPH: readonly string[] = [
  'w', // Wing
  'm', // Mandible
  'v', // Venom Gland
  'c', // Carapace
  'a', // Antenna
  'l', // Limb
];

export const GENE_TYPE_DESC: readonly string[] = [
  'A membrane scrap patterned for lift; the Brood recodes it for speed and range.',
  'A severed jaw segment, still edged; the colony remounts it onto the next body.',
  'A collapsed sac from a venomous kill, preserved intact for its toxic residue.',
  'A plate of shed or stripped chitin — dense, battle-tested armour in raw form.',
  'A sensory filament pulled from a dead scout; carries faint chemical memory.',
  'A jointed segment stripped from a limb, prized for its leverage and reach.',
];

// Primary channel each base type shapes (Wing→Movement, Mandible→Combat,
// VenomGland→Toxicity, Carapace→Defense, Antenna→Sensing, Limb→Movement).
export const GENE_TYPE_BASE_CHANNEL: readonly EffectChannel[] = [
  EFFECT_CHANNEL.Movement,  // Wing
  EFFECT_CHANNEL.Combat,    // Mandible
  EFFECT_CHANNEL.Toxicity,  // VenomGland
  EFFECT_CHANNEL.Defense,   // Carapace
  EFFECT_CHANNEL.Sensing,   // Antenna
  EFFECT_CHANNEL.Movement,  // Limb
];

// ---------------------------------------------------------------------------
// Traits (stackable mutations), indexed by GENE_TRAIT
// Lightweight=0 Reinforced=1 Toxic=2 Regenerative=3 Enlarged=4 Acute=5 Volatile=6
// ---------------------------------------------------------------------------

export const TRAIT_NAME: readonly string[] = [
  'Lightweight',
  'Reinforced',
  'Toxic',
  'Regenerative',
  'Enlarged',
  'Acute',
  'Volatile',
];

export const TRAIT_DESC: readonly string[] = [
  'The material is thinned without losing structure — the carrier moves faster but sits lighter.',
  'Dense fibres packed under compression; harder to break, heavier to haul.',
  'Residual venom has saturated the tissue — contact delivers a lingering dose.',
  'Living cells remain, knitting micro-damage between encounters.',
  'Hypertrophied growth has pushed the part beyond its original scale.',
  'Sensory dendrites have multiplied — detection range and resolution sharpen.',
  'Unstable chemistry compresses impact into a violent, uncontrolled burst.',
];

// Primary channel each trait modulates:
// Lightweight→Movement, Reinforced→Defense, Toxic→Toxicity, Regenerative→Regen,
// Enlarged→Size, Acute→Sensing, Volatile→Combat
export const TRAIT_CHANNEL: readonly EffectChannel[] = [
  EFFECT_CHANNEL.Movement,  // Lightweight
  EFFECT_CHANNEL.Defense,   // Reinforced
  EFFECT_CHANNEL.Toxicity,  // Toxic
  EFFECT_CHANNEL.Regen,     // Regenerative
  EFFECT_CHANNEL.Size,      // Enlarged
  EFFECT_CHANNEL.Sensing,   // Acute
  EFFECT_CHANNEL.Combat,    // Volatile
];

// Signed INT magnitude per stack on the primary channel (positive ~1..3).
export const TRAIT_MAGNITUDE: readonly number[] = [
  2,  // Lightweight → +2 Movement
  2,  // Reinforced  → +2 Defense
  2,  // Toxic       → +2 Toxicity
  2,  // Regenerative→ +2 Regen
  1,  // Enlarged    → +1 Size
  2,  // Acute       → +2 Sensing
  3,  // Volatile    → +3 Combat
];

// Optional secondary channel index, or -1 for none.
// Tradeoffs: Lightweight→Defense(-1), Enlarged→Movement(-1), Volatile→Defense(-2).
// Rest are -1 (no secondary channel).
export const TRAIT_CHANNEL2: readonly number[] = [
  EFFECT_CHANNEL.Defense,   // Lightweight secondary: Defense
  -1,                        // Reinforced: pure upside
  -1,                        // Toxic: pure upside
  -1,                        // Regenerative: pure upside
  EFFECT_CHANNEL.Movement,  // Enlarged secondary: Movement
  -1,                        // Acute: pure upside
  EFFECT_CHANNEL.Defense,   // Volatile secondary: Defense
];

// Signed INT on the secondary channel (negative tradeoff where set; 0 where channel2 is -1).
export const TRAIT_MAGNITUDE2: readonly number[] = [
  -1,  // Lightweight costs -1 Defense
   0,  // Reinforced: no secondary
   0,  // Toxic: no secondary
   0,  // Regenerative: no secondary
  -1,  // Enlarged costs -1 Movement
   0,  // Acute: no secondary
  -2,  // Volatile costs -2 Defense
];

// ---------------------------------------------------------------------------
// Rarity tiers, indexed by RARITY
// Common=0 Uncommon=1 Rare=2 Epic=3 Legendary=4
// ---------------------------------------------------------------------------

export const RARITY_NAME: readonly string[] = [
  'Common',
  'Uncommon',
  'Rare',
  'Epic',
  'Legendary',
];

// Tier marker chars (increasing visual prominence Common→Legendary).
export const RARITY_GLYPH: readonly string[] = [
  '.',  // Common
  '+',  // Uncommon
  '*',  // Rare
  '#',  // Epic
  '@',  // Legendary
];

// Procedural draw weights (INT, Common high → Legendary low).
export const RARITY_WEIGHT: readonly number[] = [
  60,  // Common
  25,  // Uncommon
  10,  // Rare
   4,  // Epic
   1,  // Legendary
];

// Number of trait rolls a gene of this rarity receives (more rarity ⇒ more traits).
export const RARITY_TRAIT_ROLLS: readonly number[] = [
  1,  // Common
  2,  // Uncommon
  3,  // Rare
  4,  // Epic
  5,  // Legendary
];

// Flat INT magnitude bonus added per expressed trait at this rarity.
export const RARITY_MAG_BONUS: readonly number[] = [
  0,  // Common
  1,  // Uncommon
  2,  // Rare
  3,  // Epic
  4,  // Legendary
];

// ---------------------------------------------------------------------------
// Drop weighting: which base gene type a slain caste tends to yield.
// CASTE_TYPE_WEIGHT[caste][geneType] = INT weight.
// Rows: Worker=0, Soldier=1, Flyer=2, Specialist=3  (CASTE_COUNT rows)
// Cols: Wing=0, Mandible=1, VenomGland=2, Carapace=3, Antenna=4, Limb=5  (6 cols)
//
// Flavour: Worker→Limb/Antenna, Soldier→Mandible/Carapace,
//          Flyer→Wing/Antenna,  Specialist→VenomGland/Limb.
// No column is zero — every caste can drop any part, just at low odds.
// ---------------------------------------------------------------------------

export const CASTE_TYPE_WEIGHT: readonly (readonly number[])[] = [
  // Wing  Mandible  VenomGland  Carapace  Antenna  Limb
  [   2,      3,         2,          3,       8,     12 ],  // Worker:     Limb/Antenna dominant
  [   2,     14,         3,         12,       2,      3 ],  // Soldier:    Mandible/Carapace dominant
  [  14,      2,         2,          2,      10,      3 ],  // Flyer:      Wing/Antenna dominant
  [   3,      3,        12,          2,       3,      8 ],  // Specialist: VenomGland/Limb dominant
];

// ---------------------------------------------------------------------------
// Length assertions (evaluated at import time — catch mis-edits early).
// These are pure data comparisons; no runtime cost in hot paths.
// ---------------------------------------------------------------------------

const _assertLengths = (() => {
  const GENE_TYPE_LEN = 6;
  const GENE_TRAIT_LEN = 7;
  const RARITY_LEN = 5;

  if (GENE_TYPE_NAME.length !== GENE_TYPE_LEN)
    throw new Error(`GENE_TYPE_NAME length ${GENE_TYPE_NAME.length} !== ${GENE_TYPE_LEN}`);
  if (GENE_TYPE_GLYPH.length !== GENE_TYPE_LEN)
    throw new Error(`GENE_TYPE_GLYPH length ${GENE_TYPE_GLYPH.length} !== ${GENE_TYPE_LEN}`);
  if (GENE_TYPE_DESC.length !== GENE_TYPE_LEN)
    throw new Error(`GENE_TYPE_DESC length ${GENE_TYPE_DESC.length} !== ${GENE_TYPE_LEN}`);
  if (GENE_TYPE_BASE_CHANNEL.length !== GENE_TYPE_LEN)
    throw new Error(`GENE_TYPE_BASE_CHANNEL length ${GENE_TYPE_BASE_CHANNEL.length} !== ${GENE_TYPE_LEN}`);

  if (TRAIT_NAME.length !== GENE_TRAIT_LEN)
    throw new Error(`TRAIT_NAME length ${TRAIT_NAME.length} !== ${GENE_TRAIT_LEN}`);
  if (TRAIT_DESC.length !== GENE_TRAIT_LEN)
    throw new Error(`TRAIT_DESC length ${TRAIT_DESC.length} !== ${GENE_TRAIT_LEN}`);
  if (TRAIT_CHANNEL.length !== GENE_TRAIT_LEN)
    throw new Error(`TRAIT_CHANNEL length ${TRAIT_CHANNEL.length} !== ${GENE_TRAIT_LEN}`);
  if (TRAIT_MAGNITUDE.length !== GENE_TRAIT_LEN)
    throw new Error(`TRAIT_MAGNITUDE length ${TRAIT_MAGNITUDE.length} !== ${GENE_TRAIT_LEN}`);
  if (TRAIT_CHANNEL2.length !== GENE_TRAIT_LEN)
    throw new Error(`TRAIT_CHANNEL2 length ${TRAIT_CHANNEL2.length} !== ${GENE_TRAIT_LEN}`);
  if (TRAIT_MAGNITUDE2.length !== GENE_TRAIT_LEN)
    throw new Error(`TRAIT_MAGNITUDE2 length ${TRAIT_MAGNITUDE2.length} !== ${GENE_TRAIT_LEN}`);

  if (RARITY_NAME.length !== RARITY_LEN)
    throw new Error(`RARITY_NAME length ${RARITY_NAME.length} !== ${RARITY_LEN}`);
  if (RARITY_GLYPH.length !== RARITY_LEN)
    throw new Error(`RARITY_GLYPH length ${RARITY_GLYPH.length} !== ${RARITY_LEN}`);
  if (RARITY_WEIGHT.length !== RARITY_LEN)
    throw new Error(`RARITY_WEIGHT length ${RARITY_WEIGHT.length} !== ${RARITY_LEN}`);
  if (RARITY_TRAIT_ROLLS.length !== RARITY_LEN)
    throw new Error(`RARITY_TRAIT_ROLLS length ${RARITY_TRAIT_ROLLS.length} !== ${RARITY_LEN}`);
  if (RARITY_MAG_BONUS.length !== RARITY_LEN)
    throw new Error(`RARITY_MAG_BONUS length ${RARITY_MAG_BONUS.length} !== ${RARITY_LEN}`);

  if (CASTE_TYPE_WEIGHT.length !== CASTE_COUNT)
    throw new Error(`CASTE_TYPE_WEIGHT rows ${CASTE_TYPE_WEIGHT.length} !== CASTE_COUNT ${CASTE_COUNT}`);
  for (let i = 0; i < CASTE_TYPE_WEIGHT.length; i++) {
    const row = CASTE_TYPE_WEIGHT[i] as readonly number[];
    if (row.length !== GENE_TYPE_LEN)
      throw new Error(`CASTE_TYPE_WEIGHT[${i}] length ${row.length} !== ${GENE_TYPE_LEN}`);
  }
})();
